import GenericCard from "@ui-components/Card/GenericCard";
import React from "react";
import { ReactComponent as InternetIcon } from "../../../shared/assets/Icons/IconInternet.svg";
import { ReactComponent as PortIcon } from "../../../shared/assets/Icons/IconServer.svg";
import { ReactComponent as CloudIcon } from "../../../shared/assets/Icons/IconConnectivityKontrol.svg";
import { ReactComponent as DatacenterIcon } from "../../../shared/assets/Icons/IconDataInterconnect.svg";
import { ReactComponent as FwaasIcon } from "../../../shared/assets/Icons/IconFwaas.svg";
import { ReactComponent as ArrowDown } from "../../../shared/assets/Icons/IconArrowDown.svg";
import { ReactComponent as ArrowUp } from "../../../shared/assets/Icons/IconArrowUp.svg";
import { ReactComponent as Warning } from "../../../shared/assets/Icons/Icon_Warning.svg";
import { useNavigate } from "react-router-dom";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";

export type ServiceKind = "internet" | "port" | "cloud" | "datacenter" | "fwaas";

type ServiceStatusProps = {
  title: string;
  data: { id: string; status: string; subscriptionId: string }[];
  isLoading?: boolean;
  path?: string;
  showWarning?: boolean;
  service: ServiceKind;
  related?: ServiceKind[];
  relatedPresence?: Partial<Record<ServiceKind, number>>;
};

const SERVICE_ICON: Record<ServiceKind, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  internet: InternetIcon,
  port: PortIcon,
  cloud: CloudIcon,
  datacenter: DatacenterIcon,
  fwaas: FwaasIcon,
};

// Helper: one cell for UP / WARNING / DOWN
function StatCell({
  count,
  label,
  Icon,
  classes,
  tooltipText,
}: {
  count: number;
  label: string;
  Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  classes: string;
  tooltipText?: string;
}) {
  const content = (
    <div
      className={`
        flex flex-col items-center justify-center
        ${classes} font-medium px-3 py-2
        basis-0 grow min-w-[94px] shrink-0
        min-h-[50px]
      `}
    >
      <span className="text-l font-bold leading-none">{count}</span>
      {/* Second line: Label + Icon */}
      <div className="flex items-center gap-1 mt-1">
        <span className="text-[11px] uppercase tracking-wide">{label}</span>
        {Icon ? (
          <Icon className="w-4 h-4" />
        ) : (
          <span className="w-4 h-4" />
        )}
      </div>
    </div>
  );

  return count > 0 && tooltipText ? (
    <GenericTooltip text={tooltipText} position="top">{content}</GenericTooltip>
  ) : (
    content
  );
}

export const ServiceStatusCard = ({
  title,
  data,
  isLoading = false,
  path,
  showWarning = false,
  service,
}: ServiceStatusProps) => {
  const navigate = useNavigate();

  // classify
  const UP_STATUSES = new Set(["up", "active", "connected"]);
  const DOWN_STATUSES = new Set(["down", "inactive", "disconnected"]);
  const WARNING_STATUSES = new Set(["warning"]);

  const norm = (s?: string) => (s ?? "").toString().trim().toLowerCase();

  const upItems = data.filter(i => UP_STATUSES.has(norm(i.status)));
  const downItems = data.filter(i => DOWN_STATUSES.has(norm(i.status)));
  const warningItems = data.filter(i => WARNING_STATUSES.has(norm(i.status)));

  const total = data.length;
  const upCount = upItems.length;
  const downCount = downItems.length;
  const warningCount = warningItems.length;

  // tooltips (last 5)
  const upIdsToShow = upItems.slice(-5).map(i => i.subscriptionId).join(", ");
  const downIdsToShow = downItems.slice(-5).map(i => i.subscriptionId).join(", ");
  const warningIdsToShow = warningItems.slice(-5).map(i => i.subscriptionId).join(", ");

  // colors
  const GRAY = "bg-semantic-bg-secondary text-semantic-text-secondary";
  const BRAND = "bg-semantic-bg-brand-secondary text-semantic-text-brand";
  const DANGER = "bg-semantic-bg-danger-secondary text-semantic-text-danger";
  const WARN  = "bg-semantic-bg-warning-secondary text-semantic-text-warning";

  function getStatusClasses(count: number, kind: "up" | "down" | "warning") {
    if (count <= 0) return GRAY;
    if (kind === "up") return BRAND;
    if (kind === "down") return DANGER;
    return WARN; // "warning"
  }

  const cols = showWarning ? "grid-cols-3" : "grid-cols-2";
  const MainIcon = SERVICE_ICON[service];

  return (
    <GenericCard
      isCollapsible={false}
      isLoading={isLoading}
      hoverable
      clickable={!!path}
      onClick={() => path && navigate(path)}
      bodyContent={
        <div className="flex flex-col flex-1 w-full py-1">
          <div className="flex items-start justify-start pb-2 min-h-[3rem]">
            <div className="
              flex h-10 w-10 items-center justify-center
              rounded-sm
              border border-semantic-border-secondary
              bg-semantic-bg-secondary
              text-semantic-text-secondary
              shrink-0
            ">
              <MainIcon />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-semantic-text-primary leading-tight line-clamp-2 max-h-[2.5rem] overflow-hidden">
                {title}
              </h3>
              <p className="text-sm font-medium text-semantic-text-secondary">
                Total: {total}
              </p>
            </div>
          </div>

          {/* Three cells row */}
          <div className={`grid ${cols} mt-auto pt-3 items-stretch divide-x divide-gray-300`}>
            <StatCell
              count={upCount}
              label="UP"
              Icon={ArrowUp}
              classes={getStatusClasses(upCount, "up")}
              tooltipText={upCount > 0 ? `Service ID: ${upIdsToShow}` : undefined}
            />
            {showWarning && (
            <StatCell
              count={warningCount}
              label="WARNING"
              Icon={Warning}
              classes={getStatusClasses(warningCount, "warning")}
              tooltipText={warningCount > 0 ? `Service ID: ${warningIdsToShow}` : undefined}
            />
            )}
            <StatCell
              count={downCount}
              label="DOWN"
              Icon={ArrowDown}
              classes={getStatusClasses(downCount, "down")}
              tooltipText={downCount > 0 ? `Service ID: ${downIdsToShow}` : undefined}
            />
          </div>
        </div>
        
      }
    />
  );
};
